import { useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useLocation, useNavigate, useParams } from "react-router";
import type { ReactNode } from "react";
import type { SessionDetail, SetEntry } from "@training/shared";
import { isRecord, estimateSetSeconds } from "@training/shared";
import * as api from "../lib/api.ts";
import { useSessionHue } from "../hooks/useHue.ts";
import { newClientId } from "../lib/id.ts";
import { notify, notifyError } from "../lib/notify.ts";
import { Stepper } from "../components/Stepper.tsx";
import { RepsInput } from "../components/RepsInput.tsx";
import { SideMark } from "../components/SideMark.tsx";
import { SidePicker } from "../components/SidePicker.tsx";
import { REST_PILL_ROOM, RestTimer } from "../components/RestTimer.tsx";
import { Insights } from "../components/Insight.tsx";
import { CircuitPanel } from "../components/CircuitPanel.tsx";
import { circuitAround, rowNumber } from "../lib/circuit.ts";
import { withPending } from "../lib/pending.ts";
import { supersetPartners, supersetTurn } from "../lib/superset.ts";
import { usePendingQueue } from "../hooks/usePendingQueue.ts";
import { useTimer } from "../hooks/useTimer.ts";
import { useWakeLock } from "../hooks/useWakeLock.ts";
import {
  canLog,
  cleanNote,
  targetLabel,
  defaultDropReps,
  defaultDropWeight,
  doneCount,
  dropLoadOf,
  firstRepTarget,
  format,
  formatDate,
  formatPreviousSet,
  formatSet,
  formatSetReps,
  isDropSet,
  formatRest,
  isUnilateral,
  isMyoRep,
  isMyoRepRunning,
  myoSetLabel,
  nextSetIndex,
  presetReps,
  previousFor,
  previousTopSet,
  recordSetIds,
  restAfterSet,
  rirFor,
  SHORT_REST_SECONDS,
  bothSides,
  figuresToSave,
  hasSides,
  NO_SIDE,
  otherSide,
  SIDE_NAME,
  SIDES,
  sideOf,
  switchSide,
  type RepsDraft,
  type Side,
  type SideFigures,
  type SidesDraft,
  sheetOnlySets,
  workingSets,
} from "../lib/session.ts";
import { exerciseTitle, withTransition } from "../lib/transition.ts";
import { ArrowDown, ArrowRight, ArrowUp, ArrowUpDown, ChevronLeft, Lock, Play, SlidersHorizontal } from "lucide-react";
import { rememberExercise } from "../lib/resume.ts";

/**
 * One movement, full screen. This is where the session actually happens.
 *
 * Reading order down the screen matches the order of the work: what to do,
 * what was already done, what to type next. The input sits at the bottom
 * because that is where the thumb is, and validating a set starts the rest
 * timer without another tap.
 */
export function ExerciseScreen() {
  const { id, index } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const sessionId = Number(id);
  const position = Number(index);

  const detail = useQuery({
    queryKey: ["session", sessionId],
    queryFn: () => api.getSessionDetail(sessionId),
    // With the coach logging on his phone too, his sets have to show up here
    // before this phone numbers its next one. A rest lasts a minute or more,
    // so every 30 s is enough; every 15 s kept the phone's radio awake for
    // the whole session.
    refetchInterval: 30_000,
  });
  // Held here rather than in the panel: unlocking a finished session once
  // covers every movement of it, not just the one on screen.
  const [unlocked, setUnlocked] = useState(false);
  // Held here too: the 20 s between the two halves of a superset are spent
  // walking to the partner movement, and must still count down on its screen.
  const timer = useTimer(sessionId);
  const pending = usePendingQueue();

  useWakeLock(true);

  // "Reprendre", from any other screen, comes back here.
  useEffect(() => rememberExercise(sessionId, position), [sessionId, position]);

  useSessionHue(detail.data?.session.templateId);

  if (!detail.data) return <div className="page py-20 text-ink-mute">Chargement…</div>;

  const exercises = withPending(detail.data, pending).exercises;
  const entry = exercises[position];
  if (!entry) {
    navigate(`/seance/${sessionId}`, { replace: true });
    return null;
  }

  // Carried along, so the session's way back still knows where it was opened from.
  const onNavigate = (to: number) =>
    withTransition(() => navigate(`/seance/${sessionId}/exercice/${to}`, { state: location.state }));
  const onBack = () => withTransition(() => navigate(`/seance/${sessionId}`, { state: location.state }));
  const onSaved = () => queryClient.invalidateQueries({ queryKey: ["session", sessionId] });

  // The abs circuit is one screen for all its movements; the arrows step
  // over it as a whole.
  const circuit = circuitAround(exercises, position);
  const rank = rowNumber(exercises, position);
  if (circuit) {
    const before = exercises[circuit.first - 1] ?? null;
    const after = exercises[circuit.last + 1] ?? null;
    return (
      <CircuitPanel
        key={`circuit-${circuit.first}`}
        items={circuit.items}
        editable={canLog(detail.data.session.status, unlocked)}
        onUnlock={() => setUnlocked(true)}
        timer={timer}
        number={rank.number}
        total={rank.total}
        onBack={onBack}
        onSaved={onSaved}
        footer={
          <>
            <NavButton disabled={!before} onClick={() => onNavigate(circuit.first - 1)} align="left">
              {lineName(before ?? undefined) ?? "Début"}
            </NavButton>
            <NavButton disabled={false} onClick={() => (after ? onNavigate(circuit.last + 1) : onBack())} align="right">
              {after?.exercise.name ?? "Fin de séance"}
            </NavButton>
          </>
        }
      />
    );
  }

  const turn = supersetTurn(exercises, position);

  return (
    <Panel
      key={entry.id}
      entry={entry}
      editable={canLog(detail.data.session.status, unlocked)}
      onUnlock={() => setUnlocked(true)}
      timer={timer}
      position={position}
      number={rank.number}
      total={rank.total}
      previous={lineName(exercises[position - 1])}
      next={lineName(exercises[position + 1])}
      partner={turn === null ? null : { index: turn, name: exercises[turn]!.exercise.name }}
      partners={supersetPartners(exercises, position).map((other) => other.exercise.name)}
      following={exercises[position + 1] ?? null}
      followingPartners={supersetPartners(exercises, position + 1).map((other) => other.exercise.name)}
      onNavigate={onNavigate}
      onBack={onBack}
      onSaved={onSaved}
    />
  );
}

type Entry = SessionDetail["exercises"][number];

/** What the arrows say: a circuit goes by its name, not its first movement's. */
function lineName(entry: Entry | undefined): string | null {
  return entry ? (entry.circuitGroup ?? entry.exercise.name) : null;
}

function Panel({
  entry,
  editable,
  onUnlock,
  timer,
  position,
  number,
  total,
  previous,
  next,
  partner,
  partners,
  following,
  followingPartners,
  onNavigate,
  onBack,
  onSaved,
}: {
  entry: Entry;
  editable: boolean;
  onUnlock: () => void;
  timer: ReturnType<typeof useTimer>;
  position: number;
  /** The card's place in the session list from 0, a circuit counting once. */
  number: number;
  total: number;
  previous: string | null;
  next: string | null;
  /** The superset movement whose turn it is, once this one has done its set. */
  partner: { index: number; name: string } | null;
  /** The other movements of its superset, whose turn it is or not. */
  partners: string[];
  /** The line after this one, told about once this one is through. */
  following: Entry | null;
  /** Its superset's other movements: two stations to find free, not one. */
  followingPartners: string[];
  onNavigate: (index: number) => void;
  onBack: () => void;
  onSaved: () => void;
}) {
  const settings = useQuery({ queryKey: ["settings"], queryFn: api.getSettings });
  const increment = settings.data?.plateIncrementKg ?? 2.5;
  const defaultRest = settings.data?.defaultRestSeconds ?? 30;

  const done = workingSets(entry);
  const last = done.at(-1);
  const nextIndex = nextSetIndex(entry.sets);
  const dropSet = isDropSet(entry.method);
  const unilateral = isUnilateral(entry);
  const myoRep = isMyoRep(entry.method);
  const myoRunning = isMyoRepRunning(entry);
  const setName = (setIndex: number) => myoSetLabel(entry, setIndex) ?? `Série ${setIndex}`;

  // Open on what was just lifted, else where the last session left off — the
  // coach's rule is to pick the load back up from there — and only then on
  // the load written in his sheet.
  const [weight, setWeight] = useState(last?.weightKg ?? entry.lastTime?.weightKg ?? entry.targetWeightKg ?? 20);
  const startReps =
    presetReps(entry, nextIndex) ?? last?.reps ?? firstRepTarget(entry.targetReps) ?? entry.lastTime?.reps ?? 8;
  const [draft, setDraft] = useState<RepsDraft>({
    reps: startReps,
    dropReps: defaultDropReps(entry, nextIndex) ?? startReps,
  });
  // With nothing to go by, the drop opens on the set's own load, to be
  // brought down: opening on 0, it was validated at 0 more than once.
  const [drop, setDrop] = useState(() => defaultDropWeight(entry, nextIndex) ?? weight);
  const [note, setNote] = useState("");
  const [editing, setEditing] = useState<SetEntry | null>(null);
  const [saving, setSaving] = useState(false);
  const [justSaved, setJustSaved] = useState(false);
  const [justSwitched, setJustSwitched] = useState(false);
  // A set past the prescription, asked for: the inputs come back for it.
  const [extra, setExtra] = useState(false);
  // The request takes a few milliseconds on the tailnet and the button stays
  // where it is: the second half of a shaky double tap logged a second set
  // (two "minis" 120 ms apart on 11/10). Locked for the pop's duration too,
  // and after a change of side: the same button validates the set next.
  const locked = saving || justSaved || justSwitched;

  useEffect(() => {
    if (!justSaved && !justSwitched) return;
    const reset = setTimeout(() => {
      setJustSaved(false);
      setJustSwitched(false);
    }, 400);
    return () => clearTimeout(reset);
  }, [justSaved, justSwitched]);

  // A one-sided movement is typed a side at a time: the steppers hold the
  // side picked, `sides` what was left on them for the other.
  const [sides, setSides] = useState<SidesDraft>(NO_SIDE);
  const onSteppers: SideFigures = { weightKg: weight, dropWeightKg: drop, ...draft };
  const typedSides = unilateral ? bothSides(sides, onSteppers) : null;
  const mustPickSide = unilateral && sides.active === null;
  // What the big button does first on a one-sided set: the side not typed yet.
  const sideLeft = unilateral && sides.active !== null && !typedSides ? otherSide(sides.active) : null;
  const pickSide = (side: Side) => {
    if (side === sides.active) return;
    const next = switchSide(sides, side, onSteppers, last);
    setSides(next.draft);
    setWeight(next.figures.weightKg);
    setDrop(next.figures.dropWeightKg);
    setDraft({ reps: next.figures.reps, dropReps: next.figures.dropReps });
    setJustSwitched(true);
    navigator.vibrate?.(15);
  };

  const delta = useMemo(() => {
    if (!entry.lastTime) return null;
    return Math.round((weight - entry.lastTime.weightKg) * 10) / 10;
  }, [weight, entry.lastTime]);

  const save = async () => {
    // Both sides or nothing: the buttons say so, this makes sure.
    if (unilateral && !typedSides) return;
    const left = typedSides?.left ?? onSteppers;
    const right = typedSides?.right ?? null;
    // The weaker side is the lift: 9 on one arm and 8 on the other made 8.
    const lifted = right
      ? { weightKg: Math.min(left.weightKg, right.weightKg), reps: Math.min(left.reps, right.reps) }
      : { weightKg: left.weightKg, reps: left.reps };
    setSaving(true);
    try {
      const result = await api.saveSet({
        clientId: newClientId(),
        sessionExerciseId: entry.id,
        setIndex: nextIndex,
        ...figuresToSave(left, right, dropSet),
        // The rest before it, as the timer saw it: between two sets of the movement only.
        restBeforeSeconds:
          nextIndex > 1 ? timer.taken(estimateSetSeconds({ reps: left.reps, repsRight: right?.reps ?? null })) : null,
        durationSeconds: null,
        rpe: null,
        isWarmup: false,
        notes: cleanNote(note),
      });
      setNote("");
      const earlier = done.flatMap((set) => (set.reps === null ? [] : [{ weightKg: set.weightKg, reps: set.reps }]));
      if (entry.bestLifts.length > 0 && isRecord([...entry.bestLifts, ...earlier], lifted)) {
        notify(`Record · ${format(lifted.weightKg)} kg × ${lifted.reps}`);
      }
      const preset = presetReps(entry, nextIndex + 1);
      setDraft((current) => ({ ...current, reps: preset ?? current.reps }));
      // The next set picks its side again: it does not always start on the same one.
      setSides(NO_SIDE);
      setExtra(false);
      setJustSaved(true);
      navigator.vibrate?.(result.queued ? [20, 40, 20] : 25);
      // Rest starts itself; no second tap.
      const restSeconds = restAfterSet(entry, nextIndex, defaultRest);
      if (restSeconds > 0) timer.start(restSeconds);
      if (result.queued) notify("Hors ligne : série gardée, envoyée au retour du réseau.");
      onSaved();
    } catch (error) {
      notifyError("Série non enregistrée", error);
    } finally {
      setSaving(false);
    }
  };

  // A myo-rep's "1 × X" is the activation alone: the minis have no count.
  const remaining = entry.targetSets && !myoRep ? Math.max(0, entry.targetSets - doneCount(entry)) : null;
  // Through with what was asked: the rest that follows is spent walking to
  // the next machine, so the screen tells about that one instead.
  // Passed over counts as through too: the screen moves on to the next machine.
  const skipped = entry.skippedAt !== null;
  const through = (remaining === 0 || (myoRep && entry.doneAt !== null) || skipped) && !extra;
  const fromSheet = sheetOnlySets(entry);

  // No set count and no load: a circuit like the abs line, done or not.
  const checkOnly = entry.targetSets === null && entry.targetWeightKg === null && done.length === 0;
  const [ticking, setTicking] = useState(false);
  const toggleDone = async () => {
    setTicking(true);
    try {
      await api.setExerciseDone(entry.id, !entry.doneAt);
      navigator.vibrate?.(25);
      onSaved();
    } catch (error) {
      notifyError("Pas enregistré", error);
    } finally {
      setTicking(false);
    }
  };

  const toggleSkipped = async () => {
    setTicking(true);
    try {
      await api.setExerciseSkipped(entry.id, !skipped);
      navigator.vibrate?.(25);
      onSaved();
    } catch (error) {
      notifyError("Pas enregistré", error);
    } finally {
      setTicking(false);
    }
  };

  // Failure can come on any mini, even one that made its 5: only the lifter
  // calls it. The rest that follows is the one before the next movement.
  const finishMyoRep = async () => {
    setTicking(true);
    try {
      await api.setExerciseDone(entry.id, true);
      navigator.vibrate?.(25);
      timer.start(restAfterSet(entry, 1, defaultRest));
      onSaved();
    } catch (error) {
      notifyError("Pas enregistré", error);
    } finally {
      setTicking(false);
    }
  };

  return (
    <div className="gym mx-auto flex min-h-dvh max-w-2xl flex-col px-4 md:px-8 lg:grid lg:max-w-6xl lg:grid-cols-[minmax(0,1fr)_26rem] lg:content-start lg:gap-14 lg:pt-10">
      <div>
        <header className="safe-top">
          {/* Where this movement sits, in the corner: the big ordinal it
              replaces took a line of the phone for a number. */}
          <div
            className={[
              "mt-2.5 flex items-center justify-between gap-3",
              timer.startedAt !== null ? REST_PILL_ROOM : "",
            ].join(" ")}
          >
            {/* "Séance", not its name: mid-rest the pill left it "Pecs/B…", and
                the name says nothing the list behind it does not. */}
            <button
              type="button"
              onClick={onBack}
              className="hit -ml-1 flex shrink-0 items-center gap-1 py-1 pr-2 text-[0.85rem] text-ink-mute hover:text-ink active:text-ink"
            >
              <ChevronLeft size={16} className="shrink-0" aria-hidden /> Séance
            </button>
            {/* Mid-rest the pill takes the corner: the way back keeps the room left. */}
            <span className={["label num shrink-0", timer.startedAt !== null ? "max-lg:hidden" : ""].join(" ")}>
              {number + 1} / {total}
            </span>
          </div>

          <MachineSettings
            key={entry.exercise.id}
            exercise={entry.exercise}
            onSaved={onSaved}
            title={
              <h1
                className="wide min-w-0 ink-hue text-[1.9rem] leading-tight font-bold md:text-[2.6rem]"
                style={{ viewTransitionName: exerciseTitle(entry.id) }}
              >
                {entry.exercise.name}
              </h1>
            }
            // The coach links a demo on most movements. Mid-set is exactly when
            // you want it, so it sits next to the name rather than in a menu.
            video={
              entry.videoUrl && (
                <a
                  href={entry.videoUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="hit inline-flex items-center gap-1.5 rounded-full bg-surface px-3 py-1.5 text-[0.82rem] text-ink-soft hover:text-ink active:bg-surface-high"
                >
                  <Play size={12} aria-hidden /> Voir le mouvement
                </a>
              )
            }
          />

          <Targets entry={entry} partners={partners} />
        </header>

        {!checkOnly &&
          (fromSheet.length > 0 ? (
            <SheetSlots sets={fromSheet} />
          ) : (
            <SetSlots
              sets={entry.sets}
              records={recordSetIds(entry)}
              target={entry.targetSets}
              label={setName}
              rir={(setIndex) => rirFor(entry, setIndex)}
              onEdit={editable ? setEditing : null}
            />
          ))}

        {!checkOnly && entry.previous && (
          <PreviousSets previous={entry.previous} nextSet={done.length + 1} top={previousTopSet(entry)} />
        )}
      </div>

      <div className="flex-1 lg:hidden" />

      {/* On the phone the inputs scroll with the page, and only what is
          pressed sits pinned under the thumb: the rest, and the button. All
          of the panel pinned took 70 % of the screen and hid the movement;
          none of it pinned put the button below the fold at every set. */}
      <div className="max-lg:contents lg:sticky lg:top-10 lg:mt-0 lg:space-y-2 lg:self-start">
        {editable && !checkOnly && through && following && <UpNext entry={following} partners={followingPartners} />}
        {editable && !checkOnly && !through && (
          <div className="mt-6 space-y-2 lg:mt-0">
            {unilateral && <SidePicker draft={sides} dropSet={dropSet} title={setName(nextIndex)} onPick={pickSide} />}
            {/* Out of reach until a side is picked: figures typed for no side
                  would have to be given one afterwards, by guessing. Hidden
                  rather than dimmed, in their own room so nothing moves under
                  the thumb: greyed out, they looked broken. */}
            <div className="relative">
              {mustPickSide && (
                // Last time's best stays in sight: it is read before the side is picked.
                <div className="absolute inset-0 z-10 flex flex-col items-center justify-center rounded-card border border-dashed border-line">
                  <p className="flex items-center gap-2 text-[0.95rem] font-semibold text-ink-soft">
                    <ArrowUp size={16} strokeWidth={2.5} aria-hidden /> Choisir un côté
                  </p>
                  <LoadCaption entry={entry} delta={null} listed={previousListed(entry)} />
                </div>
              )}
              <div inert={mustPickSide} className={["space-y-2", mustPickSide ? "invisible" : ""].join(" ")}>
                <Stepper
                  hero
                  label="charge"
                  unit="kg"
                  value={weight}
                  step={increment}
                  decimals={1}
                  onChange={setWeight}
                  caption={<LoadCaption entry={entry} delta={delta} listed={previousListed(entry)} />}
                />
                {dropSet && (
                  <Stepper
                    label="charge drop"
                    unit="kg"
                    value={drop}
                    step={increment}
                    decimals={1}
                    onChange={setDrop}
                  />
                )}
                <RepsInput value={draft} onChange={setDraft} dropSet={dropSet} weightKg={weight} dropWeightKg={drop} />
              </div>
            </div>
            <PreviousNote entry={entry} setIndex={nextIndex} />
            <Insights scope={`exercise/${entry.id}`} className="mt-3" />

            <NoteField
              key={nextIndex}
              value={note}
              onChange={setNote}
              placeholder={myoRep ? `Note · ${setName(nextIndex)}` : `Note pour la série ${nextIndex}`}
            />

            {/* Before the first set only: once one is in, the movement was done. */}
            {done.length === 0 && (
              <button
                type="button"
                onClick={toggleSkipped}
                disabled={ticking}
                className="h-11 w-full text-[0.82rem] text-ink-mute hover:text-ink-soft active:text-ink disabled:opacity-60"
              >
                Machine prise ? Passer ce mouvement
              </button>
            )}
          </div>
        )}

        {editable && entry.isExtra && <RemoveExtra entry={entry} onRemoved={onBack} />}

        <div className="sticky bottom-0 z-30 -mx-4 mt-3 space-y-2 border-t border-line/70 bg-base/90 px-4 pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] backdrop-blur-lg lg:static lg:mx-0 lg:mt-0 lg:border-0 lg:bg-transparent lg:p-0 lg:backdrop-blur-none">
          {timer.startedAt !== null && (
            <RestTimer
              seconds={timer.seconds}
              startedAt={timer.startedAt}
              onDone={noop}
              onDismiss={timer.stop}
              onExpire={timer.stop}
              onAdjust={timer.adjust}
            />
          )}

          {!editable ? (
            <div className="card flex items-center gap-3 px-4 py-3.5">
              <Lock size={16} className="shrink-0 text-ink-mute" aria-hidden />
              <p className="flex-1 text-[0.9rem] text-ink-soft">Séance terminée : lecture seule.</p>
              <button
                type="button"
                onClick={onUnlock}
                className="rounded-full bg-surface-high px-3.5 py-1.5 text-[0.85rem] font-semibold text-ink active:opacity-70"
              >
                Corriger
              </button>
            </div>
          ) : checkOnly ? (
            <button
              type="button"
              onClick={toggleDone}
              disabled={ticking}
              className={[
                "wide h-16 w-full rounded-card text-[1.15rem] font-bold disabled:opacity-60",
                entry.doneAt ? "border border-line bg-surface text-ink active:opacity-70" : "slab",
              ].join(" ")}
            >
              {entry.doneAt ? "Fait ✓ · toucher pour annuler" : "C'est fait"}
            </button>
          ) : (
            <>
              {/* Once the prescription is met the big button moves on instead of
                logging a set nobody asked for; an extra one stays a deliberate,
                smaller tap. */}
              {unilateral && sides.active !== null ? (
                // A set under way comes first, whatever the prescription says:
                // the other side, then the set.
                <>
                  <button
                    type="button"
                    onClick={sideLeft ? () => pickSide(sideLeft) : save}
                    disabled={locked}
                    className="wide slab h-16 w-full rounded-card text-[1.15rem] font-bold disabled:opacity-60"
                  >
                    {sideLeft ? (
                      <span className="flex items-center justify-center gap-2">
                        Passer à {SIDE_NAME[sideLeft]} <ArrowRight size={20} strokeWidth={2.5} aria-hidden />
                      </span>
                    ) : myoRep ? (
                      `Valider · ${setName(nextIndex)}`
                    ) : (
                      `Valider la série ${nextIndex}`
                    )}
                  </button>
                  {myoRunning && (
                    <button
                      type="button"
                      onClick={finishMyoRep}
                      disabled={ticking || saving}
                      className="card h-12 w-full text-[0.95rem] font-semibold text-ink active:bg-surface-high disabled:opacity-60"
                    >
                      Échec, terminé
                    </button>
                  )}
                </>
              ) : myoRunning ? (
                <>
                  <button
                    type="button"
                    onClick={save}
                    disabled={locked || mustPickSide}
                    className={[
                      "wide slab h-16 w-full rounded-card text-[1.15rem] font-bold disabled:opacity-60",
                      justSaved ? "animate-burst" : "",
                    ].join(" ")}
                  >
                    Valider · {setName(nextIndex)}
                  </button>
                  <button
                    type="button"
                    onClick={finishMyoRep}
                    disabled={ticking || saving}
                    className="card h-12 w-full text-[0.95rem] font-semibold text-ink active:bg-surface-high disabled:opacity-60"
                  >
                    Échec, terminé
                  </button>
                </>
              ) : skipped ? (
                <>
                  <button
                    type="button"
                    onClick={() => (next ? onNavigate(position + 1) : onBack())}
                    className="wide slab h-16 w-full rounded-card text-[1.15rem] font-bold"
                  >
                    {next ? "Exercice suivant" : "Retour à la séance"}
                  </button>
                  <button
                    type="button"
                    onClick={toggleSkipped}
                    disabled={ticking}
                    className="h-11 w-full text-[0.82rem] text-ink-mute hover:text-ink-soft active:text-ink disabled:opacity-60"
                  >
                    Passé · le faire quand même
                  </button>
                </>
              ) : partner ? (
                // A superset goes back and forth on every round: the partner is
                // the next thing to do, without hunting for the arrow.
                <>
                  <button
                    type="button"
                    onClick={() => onNavigate(partner.index)}
                    className={[
                      "wide slab h-16 w-full rounded-card px-4 text-[1.15rem] font-bold",
                      justSaved ? "animate-burst" : "",
                    ].join(" ")}
                  >
                    <span className="block truncate">Enchaîner · {partner.name}</span>
                  </button>
                  {/* One-sided, the side asked above is how another set starts. */}
                  {!unilateral && (
                    <button
                      type="button"
                      onClick={save}
                      disabled={locked}
                      className="h-11 w-full text-[0.82rem] text-ink-mute hover:text-ink-soft active:text-ink disabled:opacity-60"
                    >
                      + Série {nextIndex} ici
                    </button>
                  )}
                </>
              ) : through ? (
                <>
                  <button
                    type="button"
                    onClick={() => (next ? onNavigate(position + 1) : onBack())}
                    className="wide slab h-16 w-full rounded-card text-[1.15rem] font-bold"
                  >
                    {next ? "Exercice suivant" : "Retour à la séance"}
                  </button>
                  {/* Brings the inputs back rather than logging the last figures
                      blind: a set past the plan is rarely the same as the last. */}
                  <button
                    type="button"
                    onClick={() => setExtra(true)}
                    className="h-11 w-full text-[0.82rem] text-ink-mute hover:text-ink-soft active:text-ink"
                  >
                    + {myoRep ? setName(nextIndex) : `Série supplémentaire (${nextIndex})`}
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  onClick={save}
                  disabled={locked || mustPickSide}
                  className={[
                    "wide slab h-16 w-full rounded-card text-[1.15rem] font-bold disabled:opacity-60",
                    justSaved ? "animate-burst" : "",
                  ].join(" ")}
                >
                  {myoRep ? `Valider · ${setName(nextIndex)}` : `Valider la série ${nextIndex}`}
                </button>
              )}
            </>
          )}
        </div>

        {/* Once through, the button above already goes on, under the name the
            card says: the arrows only said it a third time. */}
        <div className={["flex gap-2 pt-1 safe-bottom", editable && through ? "hidden" : ""].join(" ")}>
          <NavButton disabled={!previous} onClick={() => onNavigate(position - 1)} align="left">
            {previous ?? "Début"}
          </NavButton>
          <NavButton disabled={!next} onClick={() => onNavigate(position + 1)} align="right">
            {next ?? "Fin de séance"}
          </NavButton>
        </div>
      </div>

      {editing && (
        <SetEditor
          set={editing}
          step={increment}
          withDrop={dropSet || dropLoadOf(editing) !== null}
          unilateral={unilateral || hasSides(editing)}
          onClose={() => setEditing(null)}
          onChanged={() => {
            setEditing(null);
            onSaved();
          }}
        />
      )}
    </div>
  );
}

/**
 * A note is the exception, so it waits behind a link: the field took a line
 * on every set, and every focus of it opened the full keyboard.
 */
/**
 * A movement added by mistake, or twice: taken out of the session. Only one
 * added mid-session; the coach's lines are passed over instead.
 */
function RemoveExtra({ entry, onRemoved }: { entry: Entry; onRemoved: () => void }) {
  const queryClient = useQueryClient();
  const [removing, setRemoving] = useState(false);
  const sets = entry.sets.length;

  const remove = async () => {
    const lost = sets === 0 ? "" : ` Ses ${sets} série${sets > 1 ? "s" : ""} seront supprimée${sets > 1 ? "s" : ""}.`;
    if (!window.confirm(`Retirer « ${entry.exercise.name} » de la séance ?${lost}`)) return;
    setRemoving(true);
    try {
      await api.removeSessionExercise({ sessionExerciseId: entry.id, sets });
      notify("Mouvement retiré.");
      await queryClient.invalidateQueries({ queryKey: ["session", entry.sessionId] });
      onRemoved();
    } catch (error) {
      notifyError("Mouvement non retiré", error);
      // A refusal may come from a set that landed meanwhile: show it.
      await queryClient.invalidateQueries({ queryKey: ["session", entry.sessionId] });
      setRemoving(false);
    }
  };

  return (
    <button
      type="button"
      onClick={() => void remove()}
      disabled={removing}
      className="mt-2 h-11 w-full text-[0.82rem] text-ink-mute hover:text-ink-soft active:text-ink disabled:opacity-60"
    >
      Ajouté par erreur ? Retirer de la séance
    </button>
  );
}

function NoteField({
  value,
  onChange,
  placeholder,
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
}) {
  const [open, setOpen] = useState(value !== "");
  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="flex h-11 items-center px-0.5 text-[0.85rem] text-ink-mute hover:text-ink-soft active:text-ink"
      >
        + Ajouter une note
      </button>
    );
  }
  return (
    // 16px at least: iOS zooms the page on focus of anything smaller.
    <input
      type="text"
      autoFocus
      value={value}
      onChange={(event) => onChange(event.target.value)}
      placeholder={placeholder}
      aria-label="Note"
      enterKeyHint="done"
      className="card h-11 w-full px-3.5 text-[16px] placeholder:text-ink-mute focus:border-accent focus:outline-none"
    />
  );
}

/**
 * Under the load: last time's best set, which the load opens on (the coach's
 * rule), and how today's figure compares. Not the load in his sheet: the app
 * does not write what is lifted back into it, so it lags behind the bar.
 *
 * Where last time's sets are listed beside it (`listed`, from md up), the
 * best one is marked there and only the comparison stays here.
 */
function LoadCaption({ entry, delta, listed }: { entry: Entry; delta: number | null; listed: boolean }) {
  if (!entry.lastTime) return <span className="label mt-2">premier passage</span>;

  return (
    <span className="mt-2 flex flex-col items-center gap-0.5 px-2 text-[0.82rem]">
      <span className={listed ? "text-ink-soft md:hidden" : "text-ink-soft"}>
        Top la dernière fois{" "}
        <span className="num font-semibold whitespace-nowrap text-ink">
          {format(entry.lastTime.weightKg)} kg
          {entry.lastTime.reps !== null && ` × ${entry.lastTime.reps}`}
        </span>
      </span>
      {delta !== null && delta !== 0 && (
        <span
          className={["num inline-flex items-center gap-0.5 font-bold", delta > 0 ? "text-gain" : "text-iron"].join(
            " ",
          )}
        >
          {delta > 0 ? (
            <ArrowUp size={13} strokeWidth={3} aria-hidden />
          ) : (
            <ArrowDown size={13} strokeWidth={3} aria-hidden />
          )}
          {format(Math.abs(delta))} kg
        </span>
      )}
    </span>
  );
}

/**
 * The movement after this one, once this one is through: what to set up on
 * the way there — the machine's settings, the load it opens on — read during
 * the rest rather than once arrived. The button under it goes there.
 */
function UpNext({ entry, partners }: { entry: Entry; partners: string[] }) {
  const circuit = entry.circuitGroup;
  const target = circuit ? null : targetLabel(entry);
  const settings = entry.exercise.settings;
  return (
    <section aria-label="Ensuite" className="card mt-6 px-5 py-4 lg:mt-0">
      <p className="label">Ensuite</p>
      <p className="wide mt-1 line-clamp-2 text-[1.3rem] leading-tight font-bold">{circuit ?? entry.exercise.name}</p>
      {partners.length > 0 && (
        <p className="mt-1 flex items-center gap-1.5 text-[0.88rem] text-ink-soft">
          <ArrowUpDown size={13} className="shrink-0 text-ink-mute" aria-hidden />
          en superset avec {partners.join(" et ")}
        </p>
      )}
      {/* One line: the screen above already says what an objective is. */}
      {(target || entry.lastTime) && (
        <p className="num mt-2 text-[0.95rem]">
          {target && <span className="font-semibold text-accent">{target}</span>}
          {target && entry.lastTime && <span className="text-ink-mute"> · </span>}
          {entry.lastTime && (
            <span className="text-ink-soft">
              {entry.lastTime.source === "sheet" ? "feuille" : "dernière fois"} {format(entry.lastTime.weightKg)} kg
              {entry.lastTime.reps !== null && ` × ${entry.lastTime.reps}`}
            </span>
          )}
        </p>
      )}
      {(settings || entry.videoUrl) && (
        <div className="mt-3 flex flex-wrap gap-2">
          {settings && (
            <span className="inline-flex max-w-full items-center gap-1.5 rounded-full bg-surface-high px-3 py-1.5 text-[0.82rem] text-ink-soft">
              <SlidersHorizontal size={12} aria-hidden className="shrink-0" />
              <span className="truncate">
                Réglages : <span className="num font-semibold text-ink">{settings}</span>
              </span>
            </span>
          )}
          {entry.videoUrl && (
            <a
              href={entry.videoUrl}
              target="_blank"
              rel="noreferrer"
              className="hit inline-flex items-center gap-1.5 rounded-full bg-surface-high px-3 py-1.5 text-[0.82rem] text-ink-soft hover:text-ink"
            >
              <Play size={12} aria-hidden /> Voir le mouvement
            </a>
          )}
        </div>
      )}
    </section>
  );
}

/**
 * What the coach asked for, on one line, the superset it is done in, and his
 * note under it. The RIR is
 * not part of it: each empty slot says its own, and the set about to be done
 * spells it out above the button.
 */
function Targets({ entry, partners }: { entry: Entry; partners: string[] }) {
  const target = targetLabel(entry);

  return (
    <div className="mt-4">
      <dl className="flex flex-wrap gap-x-6 gap-y-2">
        <div>
          <dt className="label">Objectif</dt>
          <dd className="num mt-1 text-[1.05rem] font-semibold text-accent">{target || "libre"}</dd>
        </div>
        {entry.restSeconds !== null && (
          <div>
            <dt className="label">Repos</dt>
            <dd className="num mt-1 text-[1.05rem] font-semibold">{restLabel(entry, entry.restSeconds)}</dd>
          </div>
        )}
      </dl>

      {partners.length > 0 && (
        <p className="mt-3 flex items-center gap-2 text-[0.9rem] text-ink-soft">
          <ArrowUpDown size={15} className="shrink-0 text-ink-mute" aria-hidden />
          <span>
            Superset avec <span className="font-semibold text-ink">{partners.join(" et ")}</span>
          </span>
        </p>
      )}

      {entry.coachNotes && (
        <p className="mt-4 border-l-2 border-accent pl-3 text-[0.88rem] text-ink-soft italic">{entry.coachNotes}</p>
      )}
    </div>
  );
}

/**
 * One slot per prescribed set. A set fills its slot as it lands; the empty
 * ones say how much is left without a number to read. Sets past the target
 * simply add slots.
 *
 * A tap on a filled slot opens it for correction — "en fait c'était 7" is far
 * more common mid-session than a set to throw away. Deleting lives in there,
 * one step further, since a set is data. `onEdit` is null when the session is
 * read-only.
 */
function SetSlots({
  sets,
  records,
  target,
  label,
  rir,
  onEdit,
}: {
  sets: SetEntry[];
  /** The sets that were personal records when lifted. */
  records: Set<string>;
  target: number | null;
  label: (setIndex: number) => string;
  /** The RIR the coach asks for that set, shown while it is still to do. */
  rir: (setIndex: number) => number | null;
  onEdit: ((set: SetEntry) => void) | null;
}) {
  const slots = Math.max(target ?? 0, sets.length);
  if (slots === 0) return <p className="mt-6 text-[0.9rem] text-ink-mute">Aucune série pour l'instant.</p>;

  const best = sets.length > 1 ? Math.max(...sets.map((set) => set.weightKg)) : null;

  return (
    <ol className="mt-5 grid grid-cols-3 gap-1.5 sm:gap-2">
      {Array.from({ length: slots }, (_, index) => {
        const set = sets[index];
        if (!set) {
          return (
            <li
              key={`empty-${index}`}
              className={[
                "flex h-14 flex-col justify-center rounded-card px-3",
                // The first empty one is the set being done.
                index === sets.length ? "slot-next" : "border border-dashed border-line",
              ].join(" ")}
            >
              <span className="label">{label(index + 1)}</span>
              {rir(index + 1) !== null && (
                <span className="num text-[0.78rem] text-ink-mute">RIR {rir(index + 1)}</span>
              )}
            </li>
          );
        }

        // Two loads and two reps a side: too long for half a phone's width.
        const wide = hasSides(set) && dropLoadOf(set) !== null;
        return (
          <li key={set.clientId} className={wide ? "col-span-2" : undefined}>
            <button
              type="button"
              disabled={!onEdit}
              onClick={() => onEdit?.(set)}
              aria-label={`${label(set.setIndex)} : ${formatSet(set, true)}.${onEdit ? " Toucher pour corriger." : ""}`}
              className={[
                "animate-fill flex min-h-14 w-full flex-col justify-center rounded-card border px-3 text-left active:opacity-70 disabled:active:opacity-100",
                set.isWarmup ? "border-line bg-surface" : "border-line bg-surface-high",
              ].join(" ")}
            >
              <span className="label flex items-center gap-1.5">
                <span className={set.isWarmup ? "" : "text-ink-soft"}>
                  {set.isWarmup ? "Échauff." : label(set.setIndex)}
                </span>
                {records.has(set.clientId) ? (
                  <span className="text-accent">· record</span>
                ) : (
                  set.weightKg === best && <span className="text-ink-mute">· top</span>
                )}
              </span>
              {hasSides(set) ? (
                // Each side on its line, in full: the load is not always the same on both.
                SIDES.map((side) => (
                  <span
                    key={side}
                    className="num flex items-center gap-1.5 text-[0.95rem] leading-snug font-bold whitespace-nowrap"
                  >
                    <SideMark side={side} size={9} /> {formatSet(sideOf(set, side))}
                  </span>
                ))
              ) : (
                <span className="mt-0.5 flex items-baseline gap-1.5">
                  <span className="display text-[1.55rem] font-bold">
                    {format(set.weightKg)}
                    {dropLoadOf(set) !== null && ` → ${format(dropLoadOf(set)!)}`}
                  </span>
                  <span className="num text-[0.95rem] text-ink-soft">× {formatSetReps(set)}</span>
                </span>
              )}
              {set.notes && <span className="block truncate text-[0.75rem] text-ink-soft italic">{set.notes}</span>}
            </button>
          </li>
        );
      })}
    </ol>
  );
}

/**
 * Correcting a set already logged: load, reps, drop, note. Same clientId, so
 * the server updates it in place, and the original time is kept — it is what
 * closes a forgotten session at the right hour.
 */
function SetEditor({
  set,
  step,
  withDrop,
  unilateral,
  onClose,
  onChanged,
}: {
  set: SetEntry;
  step: number;
  withDrop: boolean;
  unilateral: boolean;
  onClose: () => void;
  onChanged: () => void;
}) {
  // A side as the steppers take it: a figure the set does not have opens on the one before it.
  const typed = (side: Side): SideFigures => {
    const logged = sideOf(set, side);
    const reps = logged.reps ?? 0;
    return {
      weightKg: logged.weightKg,
      dropWeightKg: logged.dropWeightKg ?? logged.weightKg,
      reps,
      dropReps: logged.dropReps ?? reps,
    };
  };
  const [weight, setWeight] = useState(set.weightKg);
  const [draft, setDraft] = useState<RepsDraft>({ reps: set.reps, dropReps: set.dropReps ?? set.reps });
  const [drop, setDrop] = useState(dropLoadOf(set) ?? set.weightKg);
  // Both sides are known here, so there is nothing to pick first: it opens on the left.
  const [sides, setSides] = useState<SidesDraft>(
    unilateral ? { active: "left", typed: { right: typed("right") } } : NO_SIDE,
  );
  const onSteppers: SideFigures = { weightKg: weight, dropWeightKg: drop, ...draft };
  const pickSide = (side: Side) => {
    if (side === sides.active) return;
    const next = switchSide(sides, side, onSteppers, undefined);
    setSides(next.draft);
    setWeight(next.figures.weightKg);
    setDrop(next.figures.dropWeightKg);
    setDraft({ reps: next.figures.reps, dropReps: next.figures.dropReps });
  };
  const [note, setNote] = useState(set.notes ?? "");
  const [busy, setBusy] = useState(false);

  const run = async (action: () => Promise<{ queued: boolean }>, failure: string, offline: string) => {
    setBusy(true);
    try {
      const result = await action();
      if (result.queued) notify(offline);
      onChanged();
    } catch (error) {
      notifyError(failure, error);
    } finally {
      setBusy(false);
    }
  };

  const save = () => {
    const both = bothSides(sides, onSteppers);
    return run(
      () =>
        api.saveSet({
          clientId: set.clientId,
          sessionExerciseId: set.sessionExerciseId,
          setIndex: set.setIndex,
          ...figuresToSave(both?.left ?? onSteppers, both?.right ?? null, withDrop),
          rpe: set.rpe,
          isWarmup: set.isWarmup,
          completedAt: set.completedAt,
          durationSeconds: set.durationSeconds,
          notes: cleanNote(note),
        }),
      "Correction non enregistrée",
      "Hors ligne : correction envoyée au retour du réseau.",
    );
  };

  const remove = () => {
    if (!window.confirm(`Supprimer la série ${set.setIndex} (${formatSet(set, true)}) ?`)) return;
    void run(
      () => api.removeSet(set.clientId),
      "Série non supprimée",
      "Hors ligne : suppression envoyée au retour du réseau.",
    );
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`Corriger la série ${set.setIndex}`}
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 md:items-center"
      onClick={(event) => event.target === event.currentTarget && onClose()}
    >
      <div className="w-full max-w-md space-y-2 rounded-t-card bg-base px-4 pt-4 safe-bottom md:rounded-card md:pb-4">
        <p className="label">Corriger la série {set.setIndex}</p>
        {unilateral && (
          <SidePicker draft={sides} dropSet={withDrop} title={`Série ${set.setIndex}`} onPick={pickSide} />
        )}
        <Stepper label="charge" unit="kg" value={weight} step={step} decimals={1} onChange={setWeight} />
        {withDrop && <Stepper label="charge drop" unit="kg" value={drop} step={step} decimals={1} onChange={setDrop} />}
        <RepsInput value={draft} onChange={setDraft} dropSet={withDrop} weightKg={weight} dropWeightKg={drop} />
        <input
          type="text"
          value={note}
          onChange={(event) => setNote(event.target.value)}
          placeholder="Note (optionnel)"
          enterKeyHint="done"
          className="card h-11 w-full px-3.5 text-[16px] placeholder:text-ink-mute focus:border-accent focus:outline-none"
        />
        <button
          type="button"
          onClick={save}
          disabled={busy}
          className="wide h-14 w-full rounded-card bg-ink text-[1.05rem] font-bold text-[var(--color-base)] disabled:opacity-60"
        >
          Enregistrer
        </button>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={onClose}
            className="card h-11 flex-1 text-[0.9rem] text-ink-soft active:bg-surface-high"
          >
            Annuler
          </button>
          <button
            type="button"
            onClick={remove}
            disabled={busy}
            className="card h-11 flex-1 text-[0.9rem] text-stop active:bg-surface-high disabled:opacity-60"
          >
            Supprimer
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * How the machine is set for this movement ("-1 / 4"). Kept with the movement
 * rather than the session, so it is there every time without looking back
 * through old notes. Editable even on a closed session: it is not a set.
 *
 * It frames the title: none set yet, it is an icon beside it — a chip on its
 * own line under the name, it cost the screen a line for an empty field.
 */
function MachineSettings({
  exercise,
  onSaved,
  title,
  video,
}: {
  exercise: Entry["exercise"];
  onSaved: () => void;
  title: ReactNode;
  video: ReactNode;
}) {
  // What was last typed: a value parked offline is not in the refetch yet.
  const [saved, setSaved] = useState(exercise.settings);
  const [draft, setDraft] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (draft === null) return;
    const value = cleanNote(draft);
    setSaving(true);
    try {
      const result = await api.saveExerciseSettings(exercise.id, value);
      setSaved(value);
      setDraft(null);
      if (result.queued) notify("Hors ligne : réglages gardés, envoyés au retour du réseau.");
      onSaved();
    } catch (error) {
      notifyError("Réglages non enregistrés", error);
    } finally {
      setSaving(false);
    }
  };

  const open = () => setDraft(saved ?? "");

  return (
    <>
      <div className="mt-2 flex items-start justify-between gap-2">
        {title}
        {!saved && draft === null && (
          <button
            type="button"
            onClick={open}
            aria-label="Ajouter des réglages machine"
            className="-mr-2.5 grid size-11 shrink-0 place-items-center rounded-full text-ink-mute hover:text-ink active:bg-surface md:mt-1.5"
          >
            <SlidersHorizontal size={18} aria-hidden />
          </button>
        )}
      </div>

      {draft !== null ? (
        <SettingsForm draft={draft} saving={saving} onChange={setDraft} onSave={save} onCancel={() => setDraft(null)} />
      ) : (
        (video || saved) && (
          <div className="mt-2.5 flex flex-wrap gap-2">
            {video}
            {saved && (
              <button
                type="button"
                onClick={open}
                className="hit inline-flex max-w-full items-center gap-1.5 rounded-full bg-surface px-3 py-1.5 text-[0.82rem] text-ink-soft hover:text-ink active:bg-surface-high"
              >
                <SlidersHorizontal size={12} aria-hidden className="shrink-0" />
                <span className="truncate">
                  Réglages : <span className="num font-semibold text-ink">{saved}</span>
                </span>
              </button>
            )}
          </div>
        )
      )}
    </>
  );
}

function SettingsForm({
  draft,
  saving,
  onChange,
  onSave,
  onCancel,
}: {
  draft: string;
  saving: boolean;
  onChange: (draft: string) => void;
  onSave: () => Promise<void>;
  onCancel: () => void;
}) {
  return (
    <form
      className="mt-2.5 flex w-full items-center gap-2"
      onSubmit={(event) => {
        event.preventDefault();
        void onSave();
      }}
    >
      <input
        value={draft}
        onChange={(event) => onChange(event.target.value)}
        placeholder="Réglages machine, ex. -1 / 4"
        aria-label="Réglages machine"
        maxLength={200}
        autoFocus
        // 16px: below that, iOS zooms the page on focus.
        className="card h-11 min-w-0 flex-1 px-3.5 text-[16px] placeholder:text-ink-mute focus:border-accent focus:outline-none"
      />
      <button
        type="submit"
        disabled={saving}
        className="h-11 shrink-0 rounded-full bg-ink px-4 text-[0.82rem] font-semibold text-[var(--color-base)] disabled:opacity-50"
      >
        OK
      </button>
      <button
        type="button"
        onClick={onCancel}
        className="h-11 shrink-0 px-1 text-[0.82rem] text-ink-mute hover:text-ink"
      >
        Annuler
      </button>
    </form>
  );
}

/**
 * On the phone, where the side column does not fit: what was noted on this
 * set last time ("trop lourd"). Its load stays out — next to last time's best
 * set under the load, a second "last time" figure read as a contradiction.
 */
function PreviousNote({ entry, setIndex }: { entry: Entry; setIndex: number }) {
  const notes = previousFor(entry, setIndex)?.notes;
  if (!notes) return null;
  return (
    <p className="truncate px-0.5 text-[0.8rem] text-ink-mute md:hidden">
      Série {setIndex} la dernière fois : <span className="text-ink-soft italic">{notes}</span>
    </p>
  );
}

/**
 * A session logged in the sheet rather than the app: its loads, without reps,
 * which the sheet never recorded. Nothing to delete here — the sheet owns them.
 */
function SheetSlots({ sets }: { sets: Entry["sheetSets"] }) {
  return (
    <>
      <ol className="mt-6 grid grid-cols-2 gap-2 sm:grid-cols-3">
        {sets.map((set) => (
          <li
            key={set.setIndex}
            className="flex min-h-[4.25rem] flex-col justify-center rounded-card border border-line bg-surface-high px-3.5"
          >
            <span className="label text-ink-soft">Série {set.setIndex}</span>
            <span className="display mt-0.5 text-[1.55rem] font-bold">
              {format(set.weightKg)}
              {set.dropWeightKg !== null && ` → ${format(set.dropWeightKg)}`}
            </span>
          </li>
        ))}
      </ol>
      <p className="mt-2 text-[0.8rem] text-ink-mute">Saisie dans la feuille : charges seules, sans répétitions.</p>
    </>
  );
}

/**
 * Last time, set by set, next to today's slots. Only where the screen has room
 * for it: on the phone the load caption already says the one number that
 * matters, and the thumb area is taken. The row matching the set about to be
 * done is lit, so a long set or a note last week is read before this one.
 */
function PreviousSets({
  previous,
  nextSet,
  top,
}: {
  previous: NonNullable<Entry["previous"]>;
  nextSet: number;
  /** The set today's load opens on: the one the caption under the load names on the phone. */
  top: number | null;
}) {
  if (previous.sets.length === 0) return null;
  return (
    <section className="mt-8 hidden md:block">
      <h2 className="label">
        Séance précédente · {formatDate(previous.date)}
        {previous.source === "sheet" && " · feuille"}
      </h2>
      <ol className="mt-2 space-y-1.5">
        {previous.sets.map((set) => (
          <li
            key={set.setIndex}
            className={[
              "card flex items-baseline gap-3 px-3.5 py-2.5",
              set.setIndex === nextSet ? "border-accent" : "opacity-70",
            ].join(" ")}
          >
            <span className="label w-14 shrink-0">Série {set.setIndex}</span>
            <span className="num font-semibold">{formatPreviousSet(set)}</span>
            {set.setIndex === top && <span className="label shrink-0 text-ink-soft">top</span>}
            {set.notes && (
              <span className="min-w-0 flex-1 truncate text-[0.85rem] text-ink-soft italic">{set.notes}</span>
            )}
          </li>
        ))}
      </ol>
    </section>
  );
}

function NavButton({
  children,
  onClick,
  disabled,
  align,
}: {
  children: ReactNode;
  onClick: () => void;
  disabled: boolean;
  align: "left" | "right";
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={[
        "card h-12 min-w-0 flex-1 px-3 text-[0.85rem] text-ink-soft hover:text-ink active:bg-surface-high disabled:opacity-35",
        align === "left" ? "text-left" : "text-right",
      ].join(" ")}
    >
      <span className="block truncate">
        {align === "left" ? "‹ " : ""}
        {children}
        {align === "right" ? " ›" : ""}
      </span>
    </button>
  );
}

/** Whether `PreviousSets` has anything to show (it does from md up only). */
function previousListed(entry: Entry): boolean {
  return (entry.previous?.sets.length ?? 0) > 0 && previousTopSet(entry) !== null;
}

/** The timer announces itself; nothing else has to happen when it ends. */
function noop() {}

/** "1'30 puis 20s" on a myo-rep, "20s" before the partner of a superset. */
function restLabel(entry: Entry, seconds: number): string {
  if (isMyoRep(entry.method)) return `${formatRest(seconds)} puis ${formatRest(SHORT_REST_SECONDS)}`;
  if (entry.supersetGroup !== null && seconds === 0) return formatRest(SHORT_REST_SECONDS);
  return formatRest(seconds);
}
